import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Scale, ArrowRight, Copy, Check, Download, FileText, Loader2, History,
  CheckCircle2, Clock, DollarSign, Send, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { money, monthFmt, currentMonth } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Balance() {
  const qc = useQueryClient();
  const { user, household, role } = useAuth();
  const [copied, setCopied] = useState(false);
  const [month, setMonth] = useState(currentMonth());
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: dash } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const { data: months } = useQuery({ queryKey: ["history", "months"], queryFn: async () => (await api.get("/history/months")).data });
  const { data: hist } = useQuery({ queryKey: ["history", month], queryFn: async () => (await api.get(`/history/${month}`)).data, enabled: !!month });
  const { data: payData } = useQuery({ queryKey: ["payments"], queryFn: async () => (await api.get("/payments")).data });

  const s = dash?.summary;
  const me = dash?.me;
  const zelle = household?.zelle_handle;
  const zelleName = household?.zelle_name;

  const copyZelle = () => {
    navigator.clipboard.writeText(zelle || "");
    setCopied(true);
    toast.success("Zelle info copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const submitPayment = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/payments", { amount: amt, note: payNote, month: currentMonth() });
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Payment submitted for admin approval");
      setPayOpen(false);
      setPayAmount("");
      setPayNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  const act = async (pid, action) => {
    try {
      await api.post(`/payments/${pid}/${action}`);
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Payment ${action}d`);
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/settlements/generate", { month: currentMonth() });
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["settlements"] });
      toast.success("Settlement generated & emails sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate");
    } finally {
      setBusy(false);
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/export/pdf?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `roomiesplit-statement-${month}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("PDF export failed");
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/auth/me");
      const res = await api.get(`/export/csv?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `roomiesplit-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const payments = payData?.payments || [];
  const pending = payments.filter((p) => p.status === "submitted");

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <Scale className="h-6 w-6 text-primary" /> Balance & settlement
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">Who owes who, and how to settle up.</p>
      </div>

      <Tabs defaultValue="settle" className="w-full">
        <TabsList className="grid grid-cols-3 w-full rounded-xl h-11">
          <TabsTrigger value="settle" data-testid="tab-settle" className="rounded-lg">Settle up</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-lg">Payments{pending.length ? ` (${pending.length})` : ""}</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="rounded-lg">History</TabsTrigger>
        </TabsList>

        {/* SETTLE */}
        <TabsContent value="settle" className="space-y-4 mt-4" data-testid="monthly-settlement-hub">
          {me && (
            <div className={`rounded-3xl border p-6 text-center ${me.balance >= 0 ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30" : "border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30"}`}>
              <p className="text-sm text-muted-foreground">{monthFmt(s?.month)}</p>
              <p className={`font-heading text-xl font-bold mt-1 ${me.balance >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}>
                {me.balance >= 0.01 ? `You are owed ${money(me.balance)}` : me.balance <= -0.01 ? `You owe ${money(Math.abs(me.balance))}` : "You're all settled up"}
              </p>
              {me.balance <= -0.01 && (
                <Button onClick={() => { setPayAmount(Math.abs(me.balance).toFixed(2)); setPayOpen(true); }} className="mt-4 rounded-full font-semibold" data-testid="settle-up-zelle-btn">
                  <Send className="h-4 w-4 mr-2" /> I've paid via Zelle
                </Button>
              )}
            </div>
          )}

          {/* Zelle card */}
          <div className="rounded-3xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-3"><DollarSign className="h-4 w-4 text-primary" /><h3 className="font-heading font-bold">Household Zelle</h3></div>
            {zelle ? (
              <div className="space-y-2">
                <Row label="Recipient" value={zelleName} />
                <div className="flex items-center justify-between rounded-xl bg-secondary/60 px-3.5 py-2.5">
                  <div className="min-w-0">
                    <div className="text-[11px] text-muted-foreground">Zelle email / phone</div>
                    <div className="font-mono text-sm truncate" data-testid="zelle-handle">{zelle}</div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={copyZelle} data-testid="copy-zelle-btn" className="rounded-full shrink-0">{copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}</Button>
                </div>
                {household?.zelle_instructions && <p className="text-xs text-muted-foreground">{household.zelle_instructions}</p>}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{role === "admin" ? "Add your household's Zelle info in the Admin tab." : "Your admin hasn't set up Zelle payment info yet."}</p>
            )}
          </div>

          {/* Who owes who */}
          <div className="rounded-3xl border border-border bg-card p-5">
            <h3 className="font-heading font-bold mb-3">Who pays who</h3>
            {s?.transfers?.length ? (
              <div className="space-y-2">
                {s.transfers.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-xl bg-secondary/50 p-3" data-testid="settlement-transfer">
                    <Avatar className="h-8 w-8"><AvatarFallback className="text-xs bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">{t.from?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                    <span className="text-sm font-medium truncate flex-1">{t.from}</span>
                    <div className="flex flex-col items-center px-1">
                      <span className="font-heading font-bold text-sm tabular-nums">{money(t.amount)}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <span className="text-sm font-medium truncate flex-1 text-right">{t.to}</span>
                    <Avatar className="h-8 w-8"><AvatarFallback className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{t.to?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-2">Everyone's even — nothing to settle. 🎉</p>
            )}
          </div>

          {role === "admin" && (
            <Button onClick={generate} disabled={busy} variant="outline" className="w-full h-11 rounded-xl" data-testid="generate-settlement-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-2" /> Generate settlement & email everyone</>}
            </Button>
          )}
        </TabsContent>

        {/* PAYMENTS */}
        <TabsContent value="payments" className="space-y-3 mt-4">
          {payments.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground"><DollarSign className="h-10 w-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No payments recorded yet.</p></div>
          ) : (
            payments.map((p) => (
              <div key={p.id} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3" data-testid="payment-row">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${p.status === "approved" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : p.status === "rejected" ? "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`}>
                  {p.status === "approved" ? <CheckCircle2 className="h-5 w-5" /> : p.status === "rejected" ? <X className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><span className="font-semibold text-sm">{p.from_name}</span><Badge variant="secondary" className="text-[10px]">{p.status}</Badge></div>
                  <div className="text-xs text-muted-foreground">{monthFmt(p.month)}{p.note ? ` · ${p.note}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-bold tabular-nums">{money(p.amount)}</div>
                  {role === "admin" && p.status === "submitted" && (
                    <div className="flex gap-1 mt-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" onClick={() => act(p.id, "approve")} data-testid="approve-payment-btn"><Check className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => act(p.id, "reject")} data-testid="reject-payment-btn"><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* HISTORY */}
        <TabsContent value="history" className="space-y-4 mt-4" data-testid="monthly-history-export">
          <div className="flex items-center gap-2">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-11 rounded-xl flex-1" data-testid="history-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(months?.months?.length ? months.months : [currentMonth()]).map((m) => <SelectItem key={m} value={m}>{monthFmt(m)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={exportCsv} className="h-11 rounded-xl" data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
            <Button onClick={exportPdf} className="h-11 rounded-xl" data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
          </div>

          {hist && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <StatBox label="Total" value={money(hist.summary.total)} />
                <StatBox label="Per person" value={money(hist.summary.share)} />
                <StatBox label="Expenses" value={hist.summary.expense_count} />
              </div>
              <div className="rounded-3xl border border-border bg-card p-5">
                <h3 className="font-heading font-bold mb-3">Final balances — {monthFmt(month)}</h3>
                <div className="space-y-2">
                  {hist.summary.members.map((m) => (
                    <div key={m.member_id} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0">
                      <span className="font-medium">{m.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">paid {money(m.paid)}</span>
                        <span className={`font-semibold tabular-nums ${m.balance >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>{m.balance >= 0 ? "+" : "−"}{money(Math.abs(m.balance))}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {hist.settlement && <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Settlement generated · deadline {hist.settlement.deadline}</p>}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Payment submission dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Confirm your Zelle payment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Send {zelle ? <>via Zelle to <span className="font-mono text-foreground">{zelle}</span></> : "your payment"}, then record it here. Your admin will confirm it.</p>
            <div>
              <Label className="text-xs">Amount paid ($)</Label>
              <Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="mt-1.5 h-11 rounded-xl font-mono" data-testid="payment-amount-input" />
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="e.g. Zelle confirmation #1234" className="mt-1.5 h-11 rounded-xl" data-testid="payment-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitPayment} disabled={busy} className="w-full rounded-xl" data-testid="submit-payment-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for approval"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{label}</span><span className="text-sm font-semibold">{value}</span></div>;
}
function StatBox({ label, value }) {
  return <div className="rounded-2xl border border-border bg-card p-3.5 text-center"><div className="text-[11px] text-muted-foreground">{label}</div><div className="font-heading font-bold text-lg mt-0.5">{value}</div></div>;
}
